import {
  ClockCountdown,
  DotsThreeVertical,
  PencilSimple,
  Plus,
  Trash,
  Warning,
} from '@phosphor-icons/react';
import { useEffect, useRef, useState } from 'react';
import { ClipLoader } from 'react-spinners';
import { AccentButtonOutline } from '../../../../../../../shared/Buttons';
import {
  MaintenanceRecordWithDisplayName,
  MaintenanceReminderWithDisplayNameVIN,
} from '../../../../../../../types/apiTypes';
import { formatDate } from '../../../../../../../utils/DateUtils';
import { useScreenView } from '../../../../../../../utils/ScreenViewContext';
import { MaintenanceModal } from '../maintenance/MaintenanceModal';
import { MaintenanceAsset } from '../maintenance/types';
import useApi from '../../../../../../../utils/AxiosWrapper';
import { HttpStatusCode } from 'axios';
import toast from 'react-hot-toast';
import { createPortal } from 'react-dom';
import { formatNumberCommas } from '../../../../../../../utils/CurrencyUtils';
import { capitalize } from '../../../../../../../utils/capitalize';
export type MaintenanceReminder = {
  asset_type: string;
  vin_number: string;
  type: string;
  reminder_date: string;
  condition_miles?: number;
  current_miles?: number;
};

function MaintenanceReminders({
  addClass,
  maintenanceRemindersData,
  selectedAsset,
  maintenanceRecords,
  loadingReminders,
  assetList,
  onRecordChange,
  isPreActive,
}: {
  addClass?: string;
  maintenanceRemindersData: MaintenanceReminderWithDisplayNameVIN[];
  selectedAsset?: MaintenanceAsset;
  maintenanceRecords?: MaintenanceRecordWithDisplayName[];
  loadingReminders?: boolean;
  assetList?: MaintenanceAsset[];
  onRecordChange: () => void;
  isPreActive: boolean;
}) {
  const [popupVisibleMap, setPopupVisibleMap] = useState<Map<string, boolean>>(
    new Map()
  );
  const { callProtectedApi } = useApi();
  const { screenView } = useScreenView();
  const [loadingDeleteMap, setLoadingDeleteMap] = useState<
    Map<string, boolean>
  >(new Map());
  const [selectedMaintenanceReminder, setSelectedMaintenanceReminder] =
    useState<MaintenanceReminderWithDisplayNameVIN | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const popupRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const buttonRefs = useRef<{ [key: string]: HTMLButtonElement | null }>({});

  const togglePopup = (index: string | undefined) => {
    if (!index) return;
    setPopupVisibleMap((prevMap) => {
      const newMap = new Map(prevMap);
      newMap.set(index, !newMap.get(index)); // Toggle visibility for the item at index
      return newMap;
    });
  };

  const handleSetLoadingDelete = (loading: boolean, record_id: string) => {
    setLoadingDeleteMap((prevMap) => {
      const newMap = new Map(prevMap);
      newMap.set(record_id, loading); // Toggle visibility for the item at index
      return newMap;
    });
  };

  const deleteMaintenanceReminder = async (reminder_id: string) => {
    handleSetLoadingDelete(true, reminder_id);
    try {
      const response = await callProtectedApi(
        `/maintenanceReminders/delete/${reminder_id}`,
        'delete',
        {},
        'Error occurred while deleting maintenance reminder.'
      );
      if (response?.status === HttpStatusCode.Ok) {
        toast.success('Maintenance reminder deleted successfully');
        onRecordChange();
      }
    } catch (e: any) {
      console.error('Error occurred while deleting maintenance record: ', e);
    } finally {
      handleSetLoadingDelete(false, reminder_id);
    }
  };

  const handleClickOutside = (event: MouseEvent) => {
    const newMap = new Map(popupVisibleMap);
    let popupClicked = false;

    // Check if the click was outside any of the visible popups
    maintenanceRemindersData.forEach((record) => {
      const popupRef = popupRefs.current[record?.reminder_id ?? ''];
      const buttonRef = buttonRefs.current[record?.reminder_id ?? ''];

      if (
        (popupRef && popupRef.contains(event.target as Node)) ||
        (buttonRef && buttonRef.contains(event.target as Node))
      ) {
        popupClicked = true;
      }
    });

    // If click was outside all popups and buttons, close all popups
    if (!popupClicked) {
      maintenanceRemindersData.forEach((record) => {
        newMap.set(record?.reminder_id ?? '', false);
      });
      setPopupVisibleMap(newMap);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [popupVisibleMap]);

  const renderScreenView = (screenView: string) => {
    switch (screenView) {
      case 'desktop':
        return (
          <div
            className={`flex w-full flex-col items-center gap-x-[16px] overflow-y-auto border-[1px] border-bordergray bg-white align-middle ${addClass}`}
          >
            {((selectedAsset && selectedAsset?.asset_type?.length) ||
              assetList) && (
              <>
                <MaintenanceModal
                  kind="reminder"
                  mode="create"
                  isOpen={isModalOpen}
                  onClose={() => setIsModalOpen(false)}
                  selectedAsset={selectedAsset}
                  maintenanceRecords={
                    maintenanceRecords ??
                    ([] as MaintenanceRecordWithDisplayName[])
                  }
                  assetList={assetList}
                  onRecordChange={onRecordChange}
                />
                <MaintenanceModal
                  kind="reminder"
                  mode="edit"
                  isOpen={isEditModalOpen}
                  onClose={() => setIsEditModalOpen(false)}
                  selectedAsset={selectedAsset}
                  maintenanceRecords={
                    maintenanceRecords ??
                    ([] as MaintenanceRecordWithDisplayName[])
                  }
                  assetList={assetList}
                  onRecordChange={onRecordChange}
                  existingReminder={
                    selectedMaintenanceReminder ??
                    ({} as MaintenanceReminderWithDisplayNameVIN)
                  }
                />
              </>
            )}
            <div className="flex w-full flex-row items-center justify-between border-b-[1px] border-bordergray p-4 align-middle">
              <h2 className="text-[16px] font-semibold leading-[24px] text-primary">
                Nearest Maintenance Reminders
              </h2>
              {maintenanceRemindersData &&
                maintenanceRemindersData?.length > 0 && (
                  <AccentButtonOutline
                    onClick={() => setIsModalOpen(true)}
                    disabled={isPreActive}
                    addClass="rounded-full disabled:cursor-not-allowed disabled:opacity-60 text-primary h-[32px] w-[196px] max-w-[196px] flex flex-row items-center justify-center space-x-2 align-middle"
                  >
                    <Plus size={20} />
                    <h2 className="text-[14px]">Add Reminder</h2>
                  </AccentButtonOutline>
                )}
            </div>
            {maintenanceRemindersData &&
            maintenanceRemindersData?.length > 0 ? (
              <div className="w-full overflow-x-auto">
                <table className="min-w-full table-auto rounded-lg bg-white">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-4 py-2 text-left text-[14px] font-[600] text-primary">
                        Asset Type and VIN Number
                      </th>
                      <th className="px-4 py-2 text-left text-[14px] font-[600] text-primary">
                        Asset Display Name
                      </th>
                      <th className="px-4 py-2 text-left text-[14px] font-[600] text-primary">
                        Type
                      </th>
                      <th className="px-4 py-2 text-left text-[14px] font-[600] text-primary">
                        Date/Condition
                      </th>
                      <th className="w-[90px] px-4 py-2 text-left text-[14px] font-[600] text-primary">
                        Created By
                      </th>
                      <th className="px-4 py-2 text-right text-[14px] font-[600] text-primary">
                        Status
                      </th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody className="overflow-y-auto">
                    {maintenanceRemindersData.map((reminder, index) => (
                      <tr key={index} className="border-b border-gray-200">
                        <td className="flex flex-row items-center space-x-1 px-4 py-2 align-middle text-[14px] text-primary">
                          <img
                            alt={`${reminder?.asset_type}-svg`}
                            src={`/${reminder?.asset_type}.svg`}
                            className="h-[24px] w-[24px] text-primary"
                          />
                          <p>
                            {reminder?.asset_type &&
                              capitalize(reminder?.asset_type) +
                                ' ' +
                                reminder?.vehicle_id_number}
                          </p>
                        </td>
                        <td className="px-4 py-2 text-left text-[14px] text-primary">
                          {reminder?.asset_display_name}
                        </td>
                        <td className="px-4 py-2 text-left text-[14px] text-primary">
                          {capitalize(reminder?.maintenance_type)}
                        </td>
                        <td className="px-4 py-2 text-left text-[14px] text-primary">
                          {reminder?.reminder_type !== 'mileage'
                            ? formatDate(
                                new Date(reminder?.end_date ?? ''),
                                true
                              )
                            : formatNumberCommas(reminder?.end_mileage ?? 0) +
                              ' miles'}
                        </td>
                        <td className="w-[90px] px-4 py-2 text-left text-[14px] text-primary">
                          {reminder?.is_system_generated ? 'System' : 'User'}
                        </td>
                        <td className="px-4 py-2 text-right text-[14px] text-primary">
                          <span
                            className={`flex items-center justify-end ${reminder?.reminder_sent ? 'font-semibold text-red-500' : 'text-blue-500'}`}
                          >
                            {reminder?.reminder_sent ? 'Overdue' : 'Upcoming'}
                            {reminder?.reminder_sent ? (
                              <Warning
                                className="ml-1 text-red-500"
                                size={20}
                              />
                            ) : (
                              <ClockCountdown
                                className="ml-1 text-blue-500"
                                size={20}
                              />
                            )}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-left text-[14px] text-primary">
                          {!loadingDeleteMap.get(reminder?.reminder_id) ? (
                            <button
                              ref={(el) => {
                                buttonRefs.current[
                                  reminder?.reminder_id ?? ''
                                ] = el;
                              }}
                              onClick={() => {
                                togglePopup(reminder?.reminder_id ?? '');
                              }}
                              className="relative"
                            >
                              <DotsThreeVertical size={26} weight="bold" />
                              {popupVisibleMap.get(
                                reminder?.reminder_id ?? ''
                              ) &&
                                createPortal(
                                  <div
                                    ref={(el) => {
                                      popupRefs.current[
                                        reminder?.reminder_id ?? ''
                                      ] = el;
                                    }}
                                    className="fixed z-[9999] w-44 rounded-lg border-[1px] border-bordergray bg-white shadow-lg drop-shadow-xl"
                                    style={{
                                      left:
                                        buttonRefs.current[
                                          reminder?.reminder_id ?? ''
                                        ]?.getBoundingClientRect().left ?? 0,
                                      top:
                                        buttonRefs.current[
                                          reminder?.reminder_id ?? ''
                                        ]?.getBoundingClientRect().top ?? 0,
                                      transform:
                                        'translateX(-100%) translateX(-8px)',
                                    }}
                                  >
                                    <div>
                                      <button
                                        onClick={(e: any) => {
                                          e.stopPropagation();
                                          togglePopup(
                                            reminder?.reminder_id ?? ''
                                          );
                                          setSelectedMaintenanceReminder(
                                            reminder
                                          );
                                          setIsEditModalOpen(true);
                                        }}
                                        className="flex w-full flex-row items-center space-x-1 px-4 py-2 text-left align-middle text-primary hover:bg-gray-100"
                                      >
                                        <PencilSimple size={20} />
                                        <p>Edit</p>
                                      </button>
                                      <button
                                        onClick={async (e: any) => {
                                          e.stopPropagation();
                                          await deleteMaintenanceReminder(
                                            reminder?.reminder_id ?? ''
                                          );
                                          togglePopup(
                                            reminder?.reminder_id ?? ''
                                          );
                                        }}
                                        className="flex w-full flex-row items-center space-x-1 px-4 py-2 text-left align-middle text-red-500 hover:bg-gray-100"
                                      >
                                        <Trash size={20} />
                                        <p>Remove Record</p>
                                      </button>
                                    </div>
                                  </div>,
                                  document.body
                                )}
                            </button>
                          ) : (
                            <ClipLoader
                              color="#1D213B"
                              loading={loadingDeleteMap.get(
                                reminder?.reminder_id
                              )}
                              size={20}
                              aria-label="Loading Spinner"
                              data-testid="loader"
                              className="h-full w-full justify-center"
                            />
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : loadingReminders ? (
              <ClipLoader
                color="#1D213B"
                loading={loadingReminders}
                size={20}
                aria-label="Loading Spinner"
                data-testid="loader"
                className="mt-10 h-full w-full justify-center"
              />
            ) : (
              <div className="flex h-full w-full flex-col items-center justify-center gap-y-4 overflow-auto p-4 sm:gap-y-6 sm:p-8">
                <div className="flex flex-col items-center justify-center gap-y-1">
                  <h2 className="text-[16px] text-primary">
                    No Maintenance Reminders
                  </h2>
                  <p className="text-center text-[14px] text-graysecondary">
                    You have no maintenance reminders at the moment. All tasks
                    are up to date. If needed, you can add new reminders.
                  </p>
                </div>
                <AccentButtonOutline
                  onClick={() => setIsModalOpen(true)}
                  disabled={isPreActive}
                  addClass="shrink-0 rounded-full disabled:cursor-not-allowed disabled:opacity-60 text-primary h-[32px] w-[196px] flex flex-row items-center justify-center space-x-2 align-middle"
                >
                  <Plus size={20} />
                  <h2 className="text-[14px]">Add Maintenance</h2>
                </AccentButtonOutline>
              </div>
            )}
          </div>
        );
      case 'tablet':
        return (
          <div
            className={`flex w-full flex-col items-center gap-x-[16px] border-[1px] border-bordergray bg-white align-middle ${addClass}`}
          >
            {((selectedAsset && selectedAsset?.asset_type?.length) ||
              assetList) && (
              <>
                <MaintenanceModal
                  kind="reminder"
                  mode="create"
                  isOpen={isModalOpen}
                  onClose={() => setIsModalOpen(false)}
                  selectedAsset={selectedAsset}
                  maintenanceRecords={
                    maintenanceRecords ??
                    ([] as MaintenanceRecordWithDisplayName[])
                  }
                  assetList={assetList}
                  onRecordChange={onRecordChange}
                />
                <MaintenanceModal
                  kind="reminder"
                  mode="edit"
                  isOpen={isEditModalOpen}
                  onClose={() => setIsEditModalOpen(false)}
                  selectedAsset={selectedAsset}
                  maintenanceRecords={
                    maintenanceRecords ??
                    ([] as MaintenanceRecordWithDisplayName[])
                  }
                  assetList={assetList}
                  onRecordChange={onRecordChange}
                  existingReminder={
                    selectedMaintenanceReminder ??
                    ({} as MaintenanceReminderWithDisplayNameVIN)
                  }
                />
              </>
            )}
            <div className="flex w-full flex-row items-center justify-between border-b-[1px] border-bordergray p-4 align-middle">
              <h2 className="text-[16px] font-semibold leading-[24px] text-primary">
                Nearest Maintenance Reminders
              </h2>
              {maintenanceRemindersData &&
                maintenanceRemindersData?.length > 0 && (
                  <AccentButtonOutline
                    onClick={() => setIsModalOpen(true)}
                    addClass="rounded-full text-primary h-[32px] w-[196px] max-w-[196px] flex flex-row items-center justify-center space-x-2 align-middle"
                  >
                    <Plus size={20} />
                    <h2 className="text-[14px]">Add Reminder</h2>
                  </AccentButtonOutline>
                )}
            </div>
            {maintenanceRemindersData &&
            maintenanceRemindersData?.length > 0 ? (
              <div className="w-full overflow-x-auto">
                <table className="min-w-full table-auto rounded-lg bg-white">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-6 py-2 text-left text-[14px] font-[600] text-primary">
                        Asset Type and VIN Number
                      </th>
                      <th className="px-4 py-2 text-left text-[14px] font-[600] text-primary">
                        Asset Display Name
                      </th>
                      <th className="px-4 py-2 text-left text-[14px] font-[600] text-primary">
                        Type
                      </th>
                      <th className="px-4 py-2 text-left text-[14px] font-[600] text-primary">
                        Date/Condition
                      </th>
                      <th className="w-[90px] px-4 py-2 text-left text-[14px] font-[600] text-primary">
                        Created By
                      </th>
                      <th className="px-4 py-2 text-right text-[14px] font-[600] text-primary">
                        Status
                      </th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody className="overflow-y-auto">
                    {maintenanceRemindersData.map((reminder, index) => (
                      <tr key={index} className="border-b border-gray-200">
                        <td className="flex flex-row items-center space-x-1 px-6 py-2 align-middle text-[14px] text-primary">
                          <img
                            alt={`${reminder?.asset_type}-svg`}
                            src={`/${reminder?.asset_type}.svg`}
                            className="h-[24px] w-[24px] text-primary"
                          />
                          <p>
                            {reminder?.asset_type &&
                              capitalize(reminder?.asset_type) +
                                ' ' +
                                reminder?.vehicle_id_number}
                          </p>
                        </td>
                        <td className="px-4 py-2 text-left text-[14px] text-primary">
                          {reminder?.asset_display_name}
                        </td>
                        <td className="px-4 py-2 text-left text-[14px] text-primary">
                          {capitalize(reminder?.maintenance_type)}
                        </td>
                        <td className="px-4 py-2 text-left text-[14px] text-primary">
                          {reminder?.reminder_type !== 'mileage'
                            ? formatDate(
                                new Date(reminder?.end_date ?? ''),
                                true
                              )
                            : formatNumberCommas(reminder?.end_mileage ?? 0) +
                              ' miles'}
                        </td>
                        <td className="w-[90px] px-4 py-2 text-left text-[14px] text-primary">
                          {reminder?.is_system_generated ? 'System' : 'User'}
                        </td>
                        <td className="px-4 py-2 text-right text-[14px] text-primary">
                          <span
                            className={`flex items-center justify-end ${reminder?.reminder_sent ? 'font-semibold text-red-500' : 'text-blue-500'}`}
                          >
                            {reminder?.reminder_sent ? 'Overdue' : 'Upcoming'}
                            {reminder?.reminder_sent ? (
                              <Warning
                                className="ml-1 text-red-500"
                                size={20}
                              />
                            ) : (
                              <ClockCountdown
                                className="ml-1 text-blue-500"
                                size={20}
                              />
                            )}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-left text-[14px] text-primary">
                          {!loadingDeleteMap.get(reminder?.reminder_id) ? (
                            <button
                              ref={(el) => {
                                buttonRefs.current[
                                  reminder?.reminder_id ?? ''
                                ] = el;
                              }}
                              onClick={() => {
                                togglePopup(reminder?.reminder_id ?? '');
                              }}
                              className="relative"
                            >
                              <DotsThreeVertical size={26} weight="bold" />
                              {popupVisibleMap.get(
                                reminder?.reminder_id ?? ''
                              ) &&
                                createPortal(
                                  <div
                                    ref={(el) => {
                                      popupRefs.current[
                                        reminder?.reminder_id ?? ''
                                      ] = el;
                                    }}
                                    className="fixed z-[9999] w-44 rounded-lg border-[1px] border-bordergray bg-white shadow-lg drop-shadow-xl"
                                    style={{
                                      left:
                                        buttonRefs.current[
                                          reminder?.reminder_id ?? ''
                                        ]?.getBoundingClientRect().left ?? 0,
                                      top:
                                        buttonRefs.current[
                                          reminder?.reminder_id ?? ''
                                        ]?.getBoundingClientRect().top ?? 0,
                                      transform:
                                        'translateX(-100%) translateX(-8px)',
                                    }}
                                  >
                                    <div>
                                      <button
                                        onClick={(e: any) => {
                                          e.stopPropagation();
                                          togglePopup(
                                            reminder?.reminder_id ?? ''
                                          );
                                          setSelectedMaintenanceReminder(
                                            reminder
                                          );
                                          setIsEditModalOpen(true);
                                        }}
                                        className="flex w-full flex-row items-center space-x-1 px-4 py-2 text-left align-middle text-primary hover:bg-gray-100"
                                      >
                                        <PencilSimple size={20} />
                                        <p>Edit</p>
                                      </button>
                                      <button
                                        onClick={async (e: any) => {
                                          e.stopPropagation();
                                          await deleteMaintenanceReminder(
                                            reminder?.reminder_id ?? ''
                                          );
                                          togglePopup(
                                            reminder?.reminder_id ?? ''
                                          );
                                        }}
                                        className="flex w-full flex-row items-center space-x-1 px-4 py-2 text-left align-middle text-red-500 hover:bg-gray-100"
                                      >
                                        <Trash size={20} />
                                        <p>Remove Record</p>
                                      </button>
                                    </div>
                                  </div>,
                                  document.body
                                )}
                            </button>
                          ) : (
                            <ClipLoader
                              color="#1D213B"
                              loading={loadingDeleteMap.get(
                                reminder?.reminder_id
                              )}
                              size={20}
                              aria-label="Loading Spinner"
                              data-testid="loader"
                              className="h-full w-full justify-center"
                            />
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : loadingReminders ? (
              <ClipLoader
                color="#1D213B"
                loading={loadingReminders}
                size={20}
                aria-label="Loading Spinner"
                data-testid="loader"
                className="mt-10 h-full w-full justify-center"
              />
            ) : (
              <div className="flex h-full w-full flex-col items-center justify-center gap-y-4 overflow-auto p-4 sm:gap-y-6 sm:p-8">
                <div className="flex flex-col items-center justify-center gap-y-1">
                  <h2 className="text-[16px] text-primary">
                    No Maintenance Reminders
                  </h2>
                  <p className="text-center text-[14px] text-graysecondary">
                    You have no maintenance reminders at the moment. All tasks
                    are up to date. If needed, you can add new reminders.
                  </p>
                </div>
                <AccentButtonOutline
                  onClick={() => setIsModalOpen(true)}
                  disabled={isPreActive}
                  addClass="shrink-0 rounded-full disabled:cursor-not-allowed disabled:opacity-60 text-primary h-[32px] w-[196px] flex flex-row items-center justify-center space-x-2 align-middle"
                >
                  <Plus size={20} />
                  <h2 className="text-[14px]">Add Maintenance</h2>
                </AccentButtonOutline>
              </div>
            )}
          </div>
        );
      case 'mobile':
        return (
          <div
            className={`flex w-full flex-col items-center gap-x-[16px] border-[1px] border-bordergray bg-white align-middle ${addClass}`}
          >
            {((selectedAsset && selectedAsset?.asset_type?.length) ||
              assetList) && (
              <>
                <MaintenanceModal
                  kind="reminder"
                  mode="create"
                  isOpen={isModalOpen}
                  onClose={() => setIsModalOpen(false)}
                  selectedAsset={selectedAsset}
                  maintenanceRecords={
                    maintenanceRecords ??
                    ([] as MaintenanceRecordWithDisplayName[])
                  }
                  assetList={assetList}
                  onRecordChange={onRecordChange}
                />
                <MaintenanceModal
                  kind="reminder"
                  mode="edit"
                  isOpen={isEditModalOpen}
                  onClose={() => setIsEditModalOpen(false)}
                  selectedAsset={selectedAsset}
                  maintenanceRecords={
                    maintenanceRecords ??
                    ([] as MaintenanceRecordWithDisplayName[])
                  }
                  assetList={assetList}
                  onRecordChange={onRecordChange}
                  existingReminder={
                    selectedMaintenanceReminder ??
                    ({} as MaintenanceReminderWithDisplayNameVIN)
                  }
                />
              </>
            )}
            <div className="flex w-full flex-row items-center justify-between border-b-[1px] border-bordergray p-4 align-middle">
              <h2 className="text-[16px] font-semibold leading-[24px] text-primary">
                Nearest Maintenance Reminders
              </h2>
              {maintenanceRemindersData &&
                maintenanceRemindersData?.length > 0 && (
                  <AccentButtonOutline
                    onClick={() => setIsModalOpen(true)}
                    addClass="rounded-full text-primary h-[32px] w-[196px] max-w-[196px] flex flex-row items-center justify-center space-x-2 align-middle"
                  >
                    <Plus size={16} />
                    <h2 className="text-[14px]">Add Reminder</h2>
                  </AccentButtonOutline>
                )}
            </div>
            {maintenanceRemindersData &&
            maintenanceRemindersData?.length > 0 ? (
              <div className="w-full overflow-x-auto">
                <table className="min-w-full table-auto rounded-lg bg-white">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-6 py-2 text-left text-[14px] font-[600] text-primary">
                        Asset Type and VIN Number
                      </th>
                      <th className="px-4 py-2 text-left text-[14px] font-[600] text-primary">
                        Asset Display Name
                      </th>
                      <th className="px-4 py-2 text-left text-[14px] font-[600] text-primary">
                        Type
                      </th>
                      <th className="px-4 py-2 text-left text-[14px] font-[600] text-primary">
                        Date/Condition
                      </th>
                      <th className="w-[90px] px-4 py-2 text-left text-[14px] font-[600] text-primary">
                        Created By
                      </th>
                      <th className="px-4 py-2 text-right text-[14px] font-[600] text-primary">
                        Status
                      </th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody className="overflow-y-auto">
                    {maintenanceRemindersData.map(
                      (
                        reminder: MaintenanceReminderWithDisplayNameVIN,
                        index
                      ) => (
                        <tr key={index} className="border-b border-gray-200">
                          <td className="flex flex-row items-center space-x-1 px-6 py-2 align-middle text-[14px] text-primary">
                            <img
                              alt={`${reminder?.asset_type}-svg`}
                              src={`/${reminder?.asset_type}.svg`}
                              className="h-[24px] w-[24px] text-primary"
                            />
                            <p>
                              {reminder?.asset_type &&
                                capitalize(reminder?.asset_type) +
                                  ' ' +
                                  reminder?.vehicle_id_number}
                            </p>
                          </td>
                          <td className="px-4 py-2 text-left text-[14px] text-primary">
                            {reminder?.asset_display_name}
                          </td>
                          <td className="px-4 py-2 text-left text-[14px] text-primary">
                            {capitalize(reminder?.maintenance_type)}
                          </td>
                          <td className="px-4 py-2 text-left text-[14px] text-primary">
                            {reminder?.reminder_type !== 'mileage'
                              ? formatDate(
                                  new Date(reminder?.end_date ?? ''),
                                  true
                                )
                              : formatNumberCommas(reminder?.end_mileage ?? 0) +
                                ' miles'}
                          </td>
                          <td className="w-[90px] px-4 py-2 text-left text-[14px] text-primary">
                            {reminder?.is_system_generated ? 'System' : 'User'}
                          </td>
                          <td className="px-4 py-2 text-right text-[14px] text-primary">
                            <span
                              className={`flex items-center justify-end ${reminder?.reminder_sent ? 'font-semibold text-red-500' : 'text-blue-500'}`}
                            >
                              {reminder?.reminder_sent ? 'Overdue' : 'Upcoming'}
                              {reminder?.reminder_sent ? (
                                <Warning
                                  className="ml-1 text-red-500"
                                  size={20}
                                />
                              ) : (
                                <ClockCountdown
                                  className="ml-1 text-blue-500"
                                  size={20}
                                />
                              )}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-left text-[14px] text-primary">
                            {!loadingDeleteMap.get(reminder?.reminder_id) ? (
                              <button
                                ref={(el) => {
                                  buttonRefs.current[
                                    reminder?.reminder_id ?? ''
                                  ] = el;
                                }}
                                onClick={() => {
                                  togglePopup(reminder?.reminder_id ?? '');
                                }}
                                className="relative"
                              >
                                <DotsThreeVertical size={26} weight="bold" />
                                {popupVisibleMap.get(
                                  reminder?.reminder_id ?? ''
                                ) &&
                                  createPortal(
                                    <div
                                      ref={(el) => {
                                        popupRefs.current[
                                          reminder?.reminder_id ?? ''
                                        ] = el;
                                      }}
                                      className="fixed z-[9999] w-44 rounded-lg border-[1px] border-bordergray bg-white shadow-lg drop-shadow-xl"
                                      style={{
                                        left:
                                          buttonRefs.current[
                                            reminder?.reminder_id ?? ''
                                          ]?.getBoundingClientRect().left ?? 0,
                                        top:
                                          buttonRefs.current[
                                            reminder?.reminder_id ?? ''
                                          ]?.getBoundingClientRect().top ?? 0,
                                        transform:
                                          'translateX(-100%) translateX(-8px)',
                                      }}
                                    >
                                      <div>
                                        <button
                                          onClick={(e: any) => {
                                            e.stopPropagation();
                                            togglePopup(
                                              reminder?.reminder_id ?? ''
                                            );
                                            setSelectedMaintenanceReminder(
                                              reminder
                                            );
                                            setIsEditModalOpen(true);
                                          }}
                                          className="flex w-full flex-row items-center space-x-1 px-4 py-2 text-left align-middle text-primary hover:bg-gray-100"
                                        >
                                          <PencilSimple size={20} />
                                          <p>Edit</p>
                                        </button>
                                        <button
                                          onClick={async (e: any) => {
                                            e.stopPropagation();
                                            await deleteMaintenanceReminder(
                                              reminder?.reminder_id ?? ''
                                            );
                                            togglePopup(
                                              reminder?.reminder_id ?? ''
                                            );
                                          }}
                                          className="flex w-full flex-row items-center space-x-1 px-4 py-2 text-left align-middle text-red-500 hover:bg-gray-100"
                                        >
                                          <Trash size={20} />
                                          <p>Remove Record</p>
                                        </button>
                                      </div>
                                    </div>,
                                    document.body
                                  )}
                              </button>
                            ) : (
                              <ClipLoader
                                color="#1D213B"
                                loading={loadingDeleteMap.get(
                                  reminder?.reminder_id
                                )}
                                size={20}
                                aria-label="Loading Spinner"
                                data-testid="loader"
                                className="h-full w-full justify-center"
                              />
                            )}
                          </td>
                        </tr>
                      )
                    )}
                  </tbody>
                </table>
              </div>
            ) : loadingReminders ? (
              <ClipLoader
                color="#1D213B"
                loading={loadingReminders}
                size={20}
                aria-label="Loading Spinner"
                data-testid="loader"
                className="mt-10 h-full w-full justify-center"
              />
            ) : (
              <div className="flex h-full w-full flex-col items-center justify-center gap-y-4 overflow-auto p-4 sm:gap-y-6 sm:p-8">
                <div className="flex flex-col items-center justify-center gap-y-1">
                  <h2 className="text-[16px] text-primary">
                    No Maintenance Reminders
                  </h2>
                  <p className="text-center text-[14px] text-graysecondary">
                    You have no maintenance reminders at the moment. All tasks
                    are up to date. If needed, you can add new reminders.
                  </p>
                </div>
                <AccentButtonOutline
                  onClick={() => setIsModalOpen(true)}
                  disabled={isPreActive}
                  addClass="shrink-0 rounded-full disabled:cursor-not-allowed disabled:opacity-60 text-primary h-[32px] w-[196px] flex flex-row items-center justify-center space-x-2 align-middle"
                >
                  <Plus size={20} />
                  <h2 className="text-[14px]">Add Maintenance</h2>
                </AccentButtonOutline>
              </div>
            )}
          </div>
        );
    }
  };
  return <>{renderScreenView(screenView)}</>;
}

export default MaintenanceReminders;
